import type { ChangeEvent, CSSProperties, JSX, ReactNode, Ref } from "react";
import { useState } from "react";
import { cx } from "../cx";

export interface SwitchProps {
  label?: ReactNode | undefined;
  /** Controlled checked state. */
  checked?: boolean | undefined;
  /** Uncontrolled initial state. */
  defaultChecked?: boolean | undefined;
  onChange?: ((checked: boolean) => void) | undefined;
  disabled?: boolean | undefined;
  name?: string | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
  ref?: Ref<HTMLInputElement> | undefined;
}

/**
 * Toggle switch — blue track and volt thumb when on. A real checkbox input
 * with `role="switch"`. Ported from design/tarski-kit/components/forms/Switch.jsx.
 */
export function Switch({
  label,
  checked,
  defaultChecked = false,
  onChange,
  disabled = false,
  name,
  className,
  style,
  ref,
}: SwitchProps): JSX.Element {
  const [internal, setInternal] = useState(defaultChecked);
  const isOn = checked !== undefined ? checked : internal;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.currentTarget.checked;
    if (checked === undefined) {
      setInternal(next);
    }
    onChange?.(next);
  };

  return (
    <label className={cx("tk-switch", disabled && "tk-switch--disabled", className)} style={style}>
      <input
        ref={ref}
        type="checkbox"
        role="switch"
        aria-checked={isOn}
        className="tk-switch__input"
        checked={isOn}
        onChange={handleChange}
        disabled={disabled}
        name={name}
      />
      <span className="tk-switch__track" aria-hidden="true">
        <span className="tk-switch__thumb" />
      </span>
      {label === undefined ? null : <span className="tk-switch__label">{label}</span>}
    </label>
  );
}
